import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import WhatsAppLogo from "@/components/site/WhatsAppLogo";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * Business Hours card with live Open/Closed status.
 * Schedule (IST-based on visitor's local time):
 *   Mon – Sat: 09:00 – 20:00
 *   Sunday:    09:00 – 20:00
 * Note is shown below Sunday timing.
 */
function computeStatus() {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const hour = now.getHours();
  const min = now.getMinutes();
  const openMin = 9 * 60;
  const closeMin = day === 0 ? 20 * 60 : 20 * 60;
  const cur = hour * 60 + min;
  return cur >= openMin && cur < closeMin;
}

export default function BusinessHours({ compact = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(() => computeStatus());

  useEffect(() => {
    const id = setInterval(() => setOpen(computeStatus()), 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl shadow-sm ${compact ? "p-5" : "p-6 md:p-7"}`}
      data-testid="business-hours-card"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--steel-soft)] text-[var(--steel)]">
            <Clock size={18} />
          </span>
          <h3 className="font-display text-slate-900 text-lg md:text-xl tracking-tight">{t.hours.title}</h3>
        </div>
        <span
          data-testid="business-hours-status"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] font-grotesk font-semibold uppercase tracking-widest border ${
            open
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
          {open ? t.hours.openNow : t.hours.closedNow}
        </span>
      </div>

      <ul className="divide-y divide-slate-100">
        <li className="flex items-center justify-between py-3">
          <span className="text-slate-600 text-sm">{t.hours.mondaySaturday}</span>
          <span className="font-grotesk font-semibold text-slate-900 text-sm tabular-nums">{t.hours.timeWeekday}</span>
        </li>
        <li className="flex items-center justify-between py-3">
          <span className="text-slate-600 text-sm">{t.hours.sunday}</span>
          <span className="font-grotesk font-semibold text-slate-900 text-sm tabular-nums">{t.hours.timeSunday}</span>
        </li>
      </ul>

      <div className="mt-4 p-3.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[13px] leading-relaxed flex items-start gap-2.5">
        <WhatsAppLogo size={16} className="mt-0.5 shrink-0" />
        <span>{t.hours.note}</span>
      </div>
    </div>
  );
}
